import { array, mixed, object, string } from '@calendly/form/validation'
import { I18n } from '@calendly/i18n'
import { omit } from 'lodash-es'

import { getIsAnyCollectiveUserPools } from 'components/event_type_edit'
import { PHONE_CALL_KINDS } from 'components/event_type_edit/location_v3/constants'

import { sortHosts } from '../../utils'

const filterByPoolUid = (memberships, userPoolUid) => memberships.filter(({ user_pool_uid: uid }) => uid === userPoolUid)

export const anyMemberInUserPool = (memberships, userPool) => (
  filterByPoolUid(memberships, userPool?.uuid || userPool?.key).length === 0
)

export const transformLocationConfigurationForInitialValues = (locationConfiguration) => {
  if (!locationConfiguration || !locationConfiguration?.kind) return locationConfiguration
  const { phone_number: phoneNumber, additional_info: additionalInfo, hide_location: hideLocation } = locationConfiguration
  return {
    ...locationConfiguration,
    phoneNumber: phoneNumber || '',
    additionalInfo: additionalInfo || '',
    hideLocation: hideLocation ? 'hidden' : 'visible',
  }
}

export const getInitialValues = ({ eventType, isCreation }) => {
  const {
    isMultiHost,
    isMETParent,
    isTeam,
    location_configurations: locationConfigurations,
    memberships: initialMemberships
  } = eventType

  const transformedLocationConfigurations = locationConfigurations.map(transformLocationConfigurationForInitialValues)

  if (isMETParent) {
    return { locationConfigurations: transformedLocationConfigurations }
  }

  const sortedMemberships = sortHosts(initialMemberships.slice())
  const memberships = sortedMemberships.map(
    ({ location_configuration: locationConfiguration, ...membership }) => ({
      locationConfiguration: transformLocationConfigurationForInitialValues(locationConfiguration),
      ...membership,
      ...((isCreation && isTeam && isMultiHost) ? { enabled: true } : {}),
    })
  )

  return {
    memberships,
    locationConfigurations: transformedLocationConfigurations
  }
}

const transformLocationConfigurationForServerData = (locationConfiguration) => {
  if (!locationConfiguration || !locationConfiguration?.kind) {
    return locationConfiguration
  }

  const {
    location = null,
    phoneNumber,
    additionalInfo,
    hideLocation,
    kind: selectedKind,
    ...rest
  } = locationConfiguration

  const defaultValues = {
    kind: selectedKind,
    location: null,
    phone_number: null,
    additional_info: null,
    hide_location: false
  }
  const cleanedRest = omit(rest, ['nanoId'])
  switch (selectedKind) {
    case 'physical':
      return {
        ...cleanedRest,
        ...defaultValues,
        location,
        additional_info: additionalInfo,
      }
    case 'inbound_call':
      return {
        ...cleanedRest,
        ...defaultValues,
        additional_info: additionalInfo,
        phone_number: phoneNumber,
      }
    case 'custom':
      return {
        ...cleanedRest,
        ...defaultValues,
        location,
        hide_location: hideLocation === 'hidden',
      }
    default:
      return {
        ...cleanedRest,
        ...defaultValues,
      }
  }
}

export const formatLocationMemberships = (values) => ({
  event_type_memberships_attributes: values.memberships.map(
    ({ locationConfiguration, ...membership }) => ({
      ...membership,
      location_configuration_attributes: transformLocationConfigurationForServerData(locationConfiguration)
    })
  )
})

export const getFormatValuesToServerData = (eventType) => (values) => {
  const { isMultiHost, isMETParent } = eventType
  const { locationConfigurations, memberships } = values
  const transformedAttributes = locationConfigurations?.map(transformLocationConfigurationForServerData) || []

  const attributes = {
    hide_location: transformedAttributes?.find((attr) => !attr._destroy)?.hide_location || false,
    location_configurations_attributes: transformedAttributes
  }

  if (isMETParent) { return attributes }

  if (isMultiHost && memberships) {
    return {
      ...attributes,
      ...formatLocationMemberships(values)
    }
  }

  return attributes
}

export const getLocationKindLabel = (kindKey) => {
  if (!kindKey) return I18n.t('publisher.event_type_editor.sections.event_details.location.no_location')

  return I18n.t(`publisher.event_types.location.${kindKey}.label`)
}

const isLocationOnETLevel = (poolingType, userPools) => {
  if (poolingType === 'round_robin') { return false }

  if (poolingType === 'multi_pool' && !getIsAnyCollectiveUserPools(userPools)) { return false }

  return true
}

export const getValidationSchema = () => {
  const validations = object({
    phoneNumber: mixed()
      .when('kind', {
        is: PHONE_CALL_KINDS.inbound,
        then: string()
          .required(I18n.t('errors.event_types.phone_number'))
          .phoneNumber(I18n.t('errors.event_types.phone_number'))
      }),
    location: mixed()
      .when('kind', {
        is: 'physical',
        then: string()
          .nullable()
          .trim()
          .required(I18n.t('publisher.event_types.location.physical_location_required'))
      })
      .when('kind', {
        is: 'custom',
        then: string()
          .nullable()
          .trim()
          .required(I18n.t('publisher.event_types.location.custom_location_required'))
          .matches(/^[^<>]*$/, I18n.t('publisher.event_types.location.custom_location_invalid_character'))
      }),
    additionalInfo: mixed()
      .when('kind', {
        is: (kind) => kind === 'physical' || kind === 'inbound_call',
        then: string().trim()
      }),
    hideLocation: mixed()
      .when('kind', {
        is: 'custom',
        then: string().required().oneOf(['hidden', 'visible'])
      }),
  })

  return object({
    locationConfigurations: array()
      .when(['pooling_type', 'userPools'], {
        is: isLocationOnETLevel,
        then: array().of(validations).nullable(),
        otherwise: array()
      }),
    allHostsLocationSelection: object()
      .when(['pooling_type', 'userPools'], {
        is: isLocationOnETLevel,
        then: object(),
        otherwise: validations.nullable().default({})
      }),
    memberships: array()
      .when(['pooling_type', 'userPools'], {
        is: isLocationOnETLevel,
        then: array(),
        otherwise: array().of(
          object({
            locationConfiguration: validations.nullable().default({})
          })
        )
      })
  })
}
